import { FormattedMessage } from 'react-intl';
import { toastr } from 'react-redux-toastr';

import ConvertToApplicantModal from '../../../containers/ConvertToApplicantModal';
// eslint-disable-next-line max-len
import { MODAL_MODES } from '../../../containers/ConvertToApplicantModal/constants';
import useHasPermission from '../../../hooks/useHasPermission';
import { reloadPage } from '../../../utils/navigation-helpers';
import { REINSTATE_SCOPE } from '../constants';
import { useReinstateFromWaitlist } from '../hooks';
import messages from '../messages';

// Reinstating is an edit that also moves the positions back to WAITING: the
// agent reviews the record that was denied or cancelled, corrects whatever
// caused it, and saves. One request does both.
//
// The wizard needs applicationId and floorPlans to prefill and to submit;
// affordableWaitlistId is carried only so the success descriptor can name the
// record the caller was acting on (ENG-30212's status-dropdown bookkeeping).
const ReinstateFromWaitlist = ({
  organizationId,
  propertyId,
  applicationId,
  affordableWaitlistId,
  floorPlans,
  open,
  handleClose,
  onSuccess,
}) => {
  const { mutate: reinstateMutate, isLoading: isSubmitting } =
    useReinstateFromWaitlist({
      organizationId,
      propertyId,
      options: {
        onSuccess: () => {
          toastr.success('Success', 'Applicant reinstated successfully');
          // Deliberately no handleClose() here: handleClose means "the user
          // dismissed me", and firing it on success would clear the caller's
          // pending-action bookkeeping before onSuccess could read it — and a
          // late success from an already-dismissed modal would close whatever
          // the user has open now. Callers close the modal from onSuccess.
          onSuccess({ action: 'reinstate', affordableWaitlistId });
        },
        // Same staleness guard as EditWaitlist's onError: a 400 means the
        // record moved out from under this form (e.g. no longer Denied or
        // Canceled), so reload rather than let the agent retry against it.
        onError: (e) => {
          toastr.error('Error', e?.message ?? e);
          if (e?.statusCode === 400) {
            reloadPage();
          }
        },
      },
    });

  // A dismissed modal keeps its mutation in flight, so a caller can be handed a
  // success for an action the user already replaced. Block dismissal while the
  // request is pending; callers also verify the payload's action.
  const handleDismiss = () => {
    if (isSubmitting) return;
    handleClose();
  };

  const canAct = useHasPermission(REINSTATE_SCOPE);

  if (!open) return null;

  return (
    <ConvertToApplicantModal
      open
      mode={MODAL_MODES.REINSTATE}
      applicationId={applicationId}
      floorPlans={floorPlans}
      onClose={handleDismiss}
      onSubmit={(payload) => reinstateMutate({ applicationId, payload })}
      isSubmitting={isSubmitting}
      submitDisabledTooltip={
        canAct ? undefined : (
          <FormattedMessage {...messages.permissionRequired} />
        )
      }
    />
  );
};

export default ReinstateFromWaitlist;
